import { useState } from 'react';
import type { JudgmentProfile, JudgmentDomain } from '../../types/decisionIntelligence';
import { buildJudgmentSummary, TIER_META, topVoicesForDomain } from '../../lib/judgmentScoringEngine';

interface Props {
  profiles: JudgmentProfile[];
  currentUserId?: string;
}

const ALL_DOMAINS: { value: JudgmentDomain | 'all'; label: string }[] = [
  { value: 'all',          label: 'All domains'   },
  { value: 'product',      label: 'Product'        },
  { value: 'strategy',     label: 'Strategy'       },
  { value: 'operations',   label: 'Operations'     },
  { value: 'hiring',       label: 'Hiring'         },
  { value: 'architecture', label: 'Architecture'   },
  { value: 'gtm',          label: 'GTM'            },
  { value: 'resource',     label: 'Resource'       },
  { value: 'technical',    label: 'Technical'      },
  { value: 'general',      label: 'General'        },
];

function CalibrationBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs">—</span>;
  const pct = Math.round(score * 100);
  const color =
    pct >= 85 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
    pct >= 70 ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'        :
    pct >= 50 ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'      :
                'text-[var(--coral)] dark:text-[var(--coral)]';
  return (
    <span className={`text-sm font-bold tabular-nums ${color}`}>{pct}<span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)] font-normal">%</span></span>
  );
}

function RankRow({ rank, profile, isCurrentUser }: { rank: number; profile: JudgmentProfile; isCurrentUser: boolean }) {
  const summary = buildJudgmentSummary(profile);
  const meta    = TIER_META[summary.tier];

  const displayName = profile.email
    ? profile.email.split('@')[0].replace(/[._-]/g, ' ')
    : profile.userId.slice(0, 8);

  return (
    <div className={`flex items-center gap-3 px-4 py-3  transition-colors ${
      isCurrentUser ? 'bg-[var(--slate-dim)] border border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]/40' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
    }`}>
      {/* Rank */}
      <span className="text-xs font-bold text-[var(--text-ter)] dark:text-[var(--text-ter)] w-5 text-center tabular-nums shrink-0">
        {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : rank}
      </span>

      {/* Name + domain */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate capitalize">{displayName}</span>
          {isCurrentUser && <span className="text-[9px] text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-1.5 rounded-full">You</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
          <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">{profile.domain} · {profile.resolvedCount} resolved</span>
        </div>
      </div>

      {/* Scores */}
      <div className="flex flex-col items-end gap-0.5 shrink-0">
        <CalibrationBadge score={profile.calibrationScore} />
        <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">calibration</span>
      </div>

      {/* Proceed accuracy */}
      {profile.proceedAccuracy !== null && (
        <div className="flex flex-col items-end gap-0.5 shrink-0 hidden sm:flex">
          <span className="text-sm font-bold tabular-nums text-[var(--text-sec)] dark:text-[var(--text-pri)]">
            {Math.round(profile.proceedAccuracy * 100)}<span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-sec)] font-normal">%</span>
          </span>
          <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">proceed acc.</span>
        </div>
      )}
    </div>
  );
}

export function WhoseVoiceMatters({ profiles, currentUserId }: Props) {
  const [selectedDomain, setSelectedDomain] = useState<JudgmentDomain | 'all'>('all');

  // Filter + deduplicate: best profile per user (by calibration) if showing all
  const filtered: JudgmentProfile[] = (() => {
    if (selectedDomain === 'all') {
      const bestByUser = new Map<string, JudgmentProfile>();
      for (const p of profiles) {
        if (p.resolvedCount < 3) continue;
        const existing = bestByUser.get(p.userId);
        if (!existing || (p.calibrationScore ?? 0) > (existing.calibrationScore ?? 0)) {
          bestByUser.set(p.userId, p);
        }
      }
      return [...bestByUser.values()].sort(
        (a, b) => (b.calibrationScore ?? 0) - (a.calibrationScore ?? 0)
      );
    }
    return topVoicesForDomain(profiles, selectedDomain, 10);
  })();

  // Domains that have actual data
  const activeDomains = new Set(profiles.filter(p => p.resolvedCount > 0).map(p => p.domain));

  return (
    <div className="space-y-4">
      {/* Domain filter */}
      <div className="flex flex-wrap gap-1.5">
        {ALL_DOMAINS.filter(d => d.value === 'all' || activeDomains.has(d.value as JudgmentDomain)).map(d => (
          <button
            key={d.value}
            onClick={() => setSelectedDomain(d.value)}
            className={`px-3 py-1 text-[10px] font-medium rounded-full border transition-colors ${
              selectedDomain === d.value
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
                : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] dark:border-[var(--rule-bright)] dark:text-[var(--text-sec)] dark:hover:border-[var(--text-ter)] dark:hover:text-[var(--text-pri)]'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Header row */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3 px-4 pb-1 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)] w-5" />
          <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-1">Person</span>
          <span className="text-[9px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">Calibration</span>
        </div>
      )}

      {/* Ranked list */}
      {filtered.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[var(--text-ter)] dark:text-[var(--text-sec)] text-sm">No resolved decisions yet.</p>
          <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] text-xs mt-1">
            Scores appear once signals have been matched to outcomes.
          </p>
        </div>
      ) : (
        <div className="space-y-0.5">
          {filtered.map((p, i) => (
            <RankRow
              key={`${p.userId}-${p.domain}`}
              rank={i + 1}
              profile={p}
              isCurrentUser={p.userId === currentUserId}
            />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <p className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] px-4">
          Calibration score = 1 − mean(Brier error). Higher is better. Minimum 3 resolved decisions to appear.
        </p>
      )}
    </div>
  );
}
